import { Role } from '@/types/Role'
import { FunctionComponent, useState } from 'react'
import { Post, PostType } from '@/types/NewsFeed'
import styles from './post.module.scss'
import MediaContainer from '../../newsFeed/PostRow/mediaContainer/mediaContainer'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import Check from '@mui/icons-material/Check'
import DeletePostDialog from '../../newsFeed/Dialog/deletePostDialog.tsx'
import PostDialog from '../../newsFeed/PostRow/Dialog/postDialog.tsx'
import { Daycare } from '@/types/Daycare.ts'
import PublishPostDialog from './Dialog/publishPostDialog.tsx'
import { useTimeDifference } from '@/hooks/useTimeDifference.ts'

export interface PostDraftRowProps {
    post: Post
    role: Role
    daycare: Daycare
    refetch: () => void
    canUpdate: boolean
    canValidPosts: boolean
    isApplication: boolean
}

const PostDraftRow: FunctionComponent<PostDraftRowProps> = ({
    post,
    role,
    daycare,
    refetch,
    canUpdate,
    canValidPosts,
    isApplication
}: PostDraftRowProps) => {
    const { t } = useTranslation()

    const [openDialogDelete, setOpenDialogDelete] = useState<boolean>(false)
    const [openDialogEdit, setOpenDialogEdit] = useState<boolean>(false)
    const [openDialogPublish, setOpenDialogPublish] = useState<boolean>(false)

    const difference: string | null = useTimeDifference(post.creationDate)

    return (
        <>
            <div className={`${role === Role.parent && styles.postParent} ${styles.post}`}>
                <div className={styles.postHeader}>
                    <p className={styles.date}> {difference} </p>
                    {post.postedBy && <div className={styles.postedBy}>{`${t('by')}${post.postedBy}`}</div>}
                </div>
                <div className={styles.break}></div>
                {post.type === PostType.post && (
                    <>
                        <div className={styles.postBody}>
                            <p className={styles.text}> {post.message} </p>
                            <MediaContainer
                                mediaList={post.medias}
                                isApplication={isApplication}
                                daycareId={daycare.id}
                                postId={post.id}
                            />
                        </div>
                        {(canUpdate || canValidPosts) && (
                            <>
                                <div className={styles.break}></div>
                                <div className={styles.ButtonContainer}>
                                    {canUpdate && (
                                        <>
                                            <ButtonTextIcon
                                                type={'edit'}
                                                onClick={() => setOpenDialogEdit(true)}
                                                className={styles.Button}
                                            />
                                            <ButtonTextIcon
                                                type={'delete'}
                                                onClick={() => setOpenDialogDelete(true)}
                                                className={styles.Button}
                                            />
                                        </>
                                    )}
                                    {canValidPosts && (
                                        <ButtonTextIcon
                                            className={styles.Button}
                                            type={'custom'}
                                            customText={t('publish')}
                                            customIcon={<Check />}
                                            onClick={() => setOpenDialogPublish(true)}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
            {openDialogDelete && (
                <DeletePostDialog
                    open={openDialogDelete}
                    handleClose={() => setOpenDialogDelete(false)}
                    postId={post.id}
                    daycareId={daycare.id}
                    refetch={refetch}
                />
            )}
            {openDialogEdit && (
                <PostDialog
                    open={openDialogEdit}
                    handleClose={() => setOpenDialogEdit(false)}
                    role={role}
                    daycare={daycare}
                    modifyPost={post}
                    refetch={refetch}
                />
            )}
            {openDialogPublish && (
                <PublishPostDialog
                    open={openDialogPublish}
                    handleClose={() => setOpenDialogPublish(false)}
                    daycareId={daycare.id}
                    postId={post.id}
                    refetch={refetch}
                />
            )}
        </>
    )
}

export default PostDraftRow
